import { type ReactNode, useState } from 'react'

import { ApiError } from '../api'
import { toast, useConfirmStore } from '../hooks'
import { currentTranslation, fmt, interpolate, useT } from '../i18n'

import { Button } from './Button'
import type { ConfirmRequest } from './Confirm.types'
import { TextInput } from './Field'
import { Dialog } from './Overlay'

/** Promise-based confirmation dialog: `if (await confirm({...})) …` */
export function confirm(
  opts: Omit<ConfirmRequest, 'resolve'>,
): Promise<boolean> {
  return new Promise((resolve) =>
    useConfirmStore.getState().set({ ...opts, resolve }),
  )
}

/**
 * Standard delete flow: confirm, delete, and when the server answers 409
 * (still referenced / in use) explain why and offer "Delete anyway" (force).
 * Resolves true when something was deleted. Every delete is audited server-side.
 */
export async function confirmDelete(opts: {
  title: string
  message: ReactNode
  requireText?: string
  success: string
  run: (force: boolean) => Promise<unknown>
}): Promise<boolean> {
  const { t } = currentTranslation()

  if (
    !(await confirm({
      title: opts.title,
      message: opts.message,
      requireText: opts.requireText,
      confirmLabel: t.common.delete,
      danger: true,
    }))
  )
    return false

  try {
    await opts.run(false)
  } catch (err) {
    if (
      !(
        err instanceof ApiError &&
        err.status === 409 &&
        err.code === 'CONFLICT'
      )
    ) {
      toast.error(err)

      return false
    }

    const used = err.details.map((d) => d.message)

    const forced = await confirm({
      title: t.confirm.stillInUse,
      message: (
        <>
          <span>{err.message}</span>
          {used.length > 0 && (
            <ul className="confirm-list">
              {used.slice(0, 8).map((m, i) => (
                <li key={i}>{m}</li>
              ))}
              {used.length > 8 && (
                <li>{fmt(t.confirm.andMore, { count: used.length - 8 })}</li>
              )}
            </ul>
          )}
        </>
      ),
      confirmLabel: t.confirm.deleteAnyway,
      danger: true,
    })

    if (!forced) return false

    try {
      await opts.run(true)
    } catch (e) {
      toast.error(e)

      return false
    }
  }

  toast.success(opts.success)

  return true
}

function ConfirmDialog({
  request,
  close,
}: {
  request: ConfirmRequest
  close: (ok: boolean) => void
}) {
  const { t } = useT()
  const [typed, setTyped] = useState('')
  const blocked = !!request.requireText && typed.trim() !== request.requireText

  return (
    <Dialog
      title={request.title}
      onClose={() => close(false)}
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)}>
            {t.common.cancel}
          </Button>
          <Button
            variant={request.danger ? 'danger-solid' : 'primary'}
            onClick={() => close(true)}
            disabled={blocked}
            autoFocus={!request.requireText}
          >
            {request.confirmLabel ?? t.common.confirm}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div>{request.message}</div>
        {request.requireText && (
          <TextInput
            label={
              <>
                {interpolate(t.confirm.typeToConfirm, {
                  text: <strong>{request.requireText}</strong>,
                })}
              </>
            }
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
            autoComplete="off"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !blocked) close(true)
            }}
          />
        )}
      </div>
    </Dialog>
  )
}

export function ConfirmHost() {
  const { request, set } = useConfirmStore()

  if (!request) return null

  const close = (ok: boolean) => {
    request.resolve(ok)
    set(null)
  }

  // Keyed so the typed confirmation resets between requests.
  return (
    <ConfirmDialog
      key={request.title + String(request.requireText)}
      request={request}
      close={close}
    />
  )
}
